import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Checkbox, saveAuthReturnTo, useAuth } from '@repo/common';
import { submitPilotApplication } from '../../api/twoDays/pilotApplication.api';
import TdButton from '../../components/TwoDays/Button';
import { RadioGroup, Select, TextArea, TextInput } from '../../components/TwoDays/Modal';
import HoursRanges, { slotsError } from '../../components/TwoDays/HoursRanges';
import { cn } from '../../components/TwoDays/model/derive';
import {
  allTimezones as listTimezones,
  browserTimezone as guessTimezone,
  utcOffsetLabel as offsetOf,
} from '../../components/TwoDays/model/time';
import { SKILLS_SELF_CHECK_URL } from '../../utils/brand';

/**
 * 2days 파일럿 신청 폼 (/2days/apply, 영어는 ?lang=en).
 * 선발(3팀 구성)과 팀 매칭에 필요한 것만 받는다 — 5분 안에 끝나는 분량. 파일럿은 무료다.
 * 코레디 계정이 없어도 신청할 수 있다: 계정이 있으면 로그인한 계정 이메일, 없으면 직접 입력한 이메일로 받는다.
 * 문구는 COPY 에 언어별로 모은다. 서버로 보내는 값은 언어와 상관없이 같은 키를 쓴다.
 */


/** 운영 안내 (Notion) — 14번 '기준 보기'는 10. 이탈 기준(·11. 완주) 위치로 바로 연다 */
const GUIDE_URL =
  'https://southern-basketball-646.notion.site/2days-project-3f8392396e9e8142bab0c9d33ff6d52e';
const GUIDE_RULES_URL = `${GUIDE_URL}#2468bf2f8b244a919263bdad98c60fcd`;

/** 필수 동의 (신청서 최종 동의서 2026-10-13) — 11 만 18세 · 12 약관 · 13 신청 정보 · 14 이탈·완주 기준 · 15 파일럿 */
const REQUIRED_CHECKS = [
  'ageConfirmed',
  'termsAgreed',
  'dataConsent',
  'rulesConfirmed',
  'pilotUnderstood',
];
const ALL_CHECKS = [...REQUIRED_CHECKS, 'marketingOptIn'];

/** 참여 가능 일정 — 값은 시작일(KST). 일정마다 49시간이라 3일째에 끝난다 */
const DATE_SLOTS = [
  { value: '2026-11-04', ko: '11/4(수) – 11/6(금)', en: 'Nov 4 (Wed) – Nov 6 (Fri)' },
  { value: '2026-11-05', ko: '11/5(목) – 11/7(토)', en: 'Nov 5 (Thu) – Nov 7 (Sat)' },
  { value: '2026-11-11', ko: '11/11(수) – 11/13(금)', en: 'Nov 11 (Wed) – Nov 13 (Fri)' },
  { value: '2026-11-12', ko: '11/12(목) – 11/14(토)', en: 'Nov 12 (Thu) – Nov 14 (Sat)' },
];

const COPY = {
  ko: {
    title: '2days project 파일럿 신청',
    intro:
      '2일 동안 팀에 합류해 실제로 일하는 방식을 보여 주세요.\n이번 파일럿은 무료예요. 신청은 5분이면 끝나요.',
    fullName: '이름',
    fullNamePlaceholder: '예: Nguyen Thi Lan',
    hasAccount: '코레디 계정이 있나요?',
    yesNo: [
      { value: 'yes', label: '네' },
      { value: 'no', label: '아니요' },
    ],
    login: '코레디 계정으로 로그인',
    accountEmail: (email) => `코레디 계정 이메일(${email})로 신청돼요.`,
    email: '선발 결과를 받을 이메일',
    emailHint: '선발 결과를 이 이메일로 보내 드려요.',
    role: '어떤 역할로 참여하고 싶나요?',
    roles: {
      planner: '기획자 (PM)',
      designer: '디자이너',
      developer: '개발자',
      marketer: '마케터',
    },
    experience: '이 역할로 일한 경험이 얼마나 되나요?',
    experiences: {
      student: '학생이거나 실무 경험 없음',
      under_1: '1년 미만',
      '1_3': '1~3년',
      over_3: '3년 초과',
    },
    tools: '어떤 툴을 사용하나요?',
    toolsHint: '해당하는 것을 모두 골라 주세요.',
    other: '기타',
    toolsOtherPlaceholder: '사용하는 툴을 적어 주세요 (쉼표로 구분)',
    referralOtherPlaceholder: '어디서 알게 되었는지 적어 주세요',
    languages: '어떤 언어로 일할 수 있나요?',
    languagesHint: '해당하는 언어를 모두 고르고, 언어마다 수준을 골라 주세요.',
    languageNames: { English: '영어', Korean: '한국어' },
    languageOtherPlaceholder: '언어 이름',
    level: '수준',
    levels: { Basic: '기초', Conversational: '일상 대화', Fluent: '유창함' },
    dates: '참여할 수 있는 일정을 모두 골라 주세요.',
    datesHint:
      '고른 일정에는 2일 내내 참여할 수 있어야 해요. 모든 날짜·시각은 한국 시간(KST) 기준이에요.',
    hours: '하루에 작업할 수 있는 시간을 알려 주세요.',
    hoursHint: '내 시간대 기준으로 골라 주세요. 팀원에게는 한국 시간으로 보여요.',
    timezone: '시간대',
    from: '시작',
    to: '종료',
    addHours: '+ 시간대 추가',
    removeHours: '시간대 삭제',
    motivation: '이번 프로젝트로 증명하고 싶은 것은 무엇인가요?',
    motivationHint: '300자까지 쓸 수 있어요.',
    motivationPlaceholder:
      '예: 거친 아이디어를 개발자가 바로 만들 수 있는 디자인으로 바꿀 수 있어요.',
    referral: '코레디를 어떻게 알게 되었나요?',
    optional: '선택',
    select: '선택해 주세요',
    referrals: {
      instagram: '인스타그램',
      meta_ad: '메타 광고',
      friend: '지인 소개',
      community: '온라인 커뮤니티',
      other: '기타',
    },
    confirm: '신청 전에 확인해 주세요',
    replyNotice:
      '선발되면 이메일을 보낸 때부터 24시간 안에 답장해 주세요. 답장이 없으면 다른 지원자에게 기회가 넘어갈 수 있어요.',
    tags: { all: '전체 동의', required: '필수', optional: '선택' },
    agreeAll: '아래 내용에 모두 동의해요. (선택 항목 포함)',
    ageConfirmed: '만 18세 이상이에요.',
    termsAgreed: '2days project 이용약관(운영 안내 포함)에 동의해요.',
    termsLink: '이용약관',
    guideLink: '운영 안내',
    dataConsent: '선발을 위한 신청 정보 수집·이용에 동의해요.',
    privacyLink: '개인정보처리방침',
    viewDetails: '자세히 보기',
    hideDetails: '접기',
    dataTable: {
      headers: ['수집 항목', '이용 목적', '보유 기간'],
      row: [
        '이름, 이메일, 코레디 계정 보유 여부, 희망 역할, 경력 수준, 사용 툴·기술 스택, 협업 가능 언어·수준, 참여 가능 일정, 시간대와 하루 작업 가능 시각, 지원 동기, 만 18세 이상 확인 여부, 유입 경로(선택)',
        '참여자 선발, 팀 구성, 선발 결과·일정 안내, 참여 의사 확인',
        '선발 종료 후 6개월. 선발되지 않았거나 24시간 안에 답장이 없어 선발이 취소된 경우 바로 파기',
      ],
      refuse: '동의하지 않을 수 있지만, 그 경우 신청할 수 없어요.',
    },
    rulesConfirmed: '운영 안내의 이탈·완주 기준을 확인했어요.',
    rulesLink: '기준 보기',
    pilotUnderstood: '무료로 진행하는 파일럿 테스트라는 점을 이해했어요.',
    pilotDetail: '일부 기능이 완성되지 않았을 수 있고, 끝난 뒤 사용 후기를 요청받을 수 있어요.',
    marketingOptIn: '다음 프로그램 모집 등 코레디의 소식을 이메일로 받을게요.',
    marketingDetail: '언제든 수신을 거부할 수 있어요. 동의하지 않아도 신청에는 영향이 없어요.',
    submitHint: '필수 항목에 모두 체크해 주세요.',
    submit: '신청하기',
    submitting: '신청하는 중…',
    errors: {
      required: '이 질문에 답해 주세요.',
      login: '코레디 계정으로 로그인해 주세요.',
      email: '올바른 이메일 주소를 입력해 주세요.',
      dates: '일정을 1개 이상 골라 주세요.',
      level: '고른 언어의 수준을 골라 주세요.',
      hours: '종료 시각은 시작 시각보다 늦어야 해요.',
      hoursSame: '시작과 종료 시각이 같을 수 없어요.',
      hoursOverlap: '시간대가 서로 겹치지 않게 골라 주세요.',
      tooLong: '300자 이내로 써 주세요.',
      check: '이 항목에 체크해야 신청할 수 있어요.',
      duplicate: '이미 이 이메일로 신청했어요. 선발 결과는 이메일로 알려 드릴게요.',
      server: '신청서를 보내지 못했어요. 인터넷 연결을 확인하고 다시 시도해 주세요.',
    },
    done: {
      title: '신청이 접수됐어요',
      // 선발 결과는 신청 뒤 영업일 기준 7일 안에 알린다
      body: '영업일 기준 7일 안에 선발 결과와 확정된 일정을 이메일로 알려 드릴게요. 선발되면 24시간 안에 답장해 주세요. 답장이 없으면 다른 지원자에게 기회가 넘어갈 수 있어요.',
      cta: '3분 자가진단 하기',
    },
  },
  en: {
    title: 'Apply for the 2days project pilot',
    intro:
      'Join a team for 2 days and show how you really work.\nThe pilot is free. Applying takes about 5 minutes.',
    fullName: 'Full name',
    fullNamePlaceholder: 'e.g. Nguyen Thi Lan',
    hasAccount: 'Do you have a Koready account?',
    yesNo: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
    login: 'Log in with Koready',
    accountEmail: (email) => `We'll use your Koready account email (${email}).`,
    email: 'Email',
    emailHint: "We'll send your result to this email.",
    role: 'Which role do you want to take?',
    roles: {
      planner: 'Planner (PM)',
      designer: 'Designer',
      developer: 'Developer',
      marketer: 'Marketer',
    },
    experience: 'How much experience do you have in this role?',
    experiences: {
      student: 'Student or no work experience',
      under_1: 'Less than 1 year',
      '1_3': '1–3 years',
      over_3: 'More than 3 years',
    },
    tools: 'Which tools do you use?',
    toolsHint: 'Select all that apply.',
    other: 'Other',
    toolsOtherPlaceholder: 'Enter your tools (separate with commas)',
    referralOtherPlaceholder: 'Tell us where you heard about us',
    languages: 'Which languages can you work in?',
    languagesHint: 'Select all that apply, and choose your level for each.',
    languageNames: { English: 'English', Korean: 'Korean' },
    languageOtherPlaceholder: 'Language',
    level: 'Level',
    levels: { Basic: 'Basic', Conversational: 'Conversational', Fluent: 'Fluent' },
    dates: 'Which dates can you join?',
    datesHint:
      'Select all dates that work for you. You need to be available for the full 2 days. All dates and times are in Korea time (KST).',
    hours: 'When can you work each day?',
    hoursHint: 'Use your local time. Your teammates will see it in Korea time.',
    timezone: 'Time zone',
    from: 'From',
    to: 'to',
    addHours: '+ Add time range',
    removeHours: 'Remove time range',
    motivation: 'What do you want to prove through this project?',
    motivationHint: 'Up to 300 characters.',
    motivationPlaceholder: 'e.g. I can turn a rough idea into a design that developers can build.',
    referral: 'How did you hear about us?',
    optional: 'optional',
    select: 'Select',
    referrals: {
      instagram: 'Instagram',
      meta_ad: 'Meta ad',
      friend: 'A friend',
      community: 'Online community',
      other: 'Other',
    },
    confirm: 'Before you apply',
    replyNotice:
      "If you're selected, please reply to our email within 24 hours. If we don't hear from you, we may offer your spot to another applicant.",
    tags: { all: 'Agree to all', required: 'Required', optional: 'Optional' },
    agreeAll: 'I agree to everything below, including optional items.',
    ageConfirmed: 'I am 18 or older.',
    termsAgreed: 'I agree to the 2days project Terms of Use, including the Program Guide.',
    termsLink: 'Terms of Use',
    guideLink: 'Program Guide',
    dataConsent: 'I agree to the collection and use of my application data for selection.',
    privacyLink: 'Privacy Policy',
    viewDetails: 'View details',
    hideDetails: 'Hide details',
    dataTable: {
      headers: ['What we collect', 'Why we use it', 'How long we keep it'],
      row: [
        'Name, email, whether you have a Koready account, preferred role, experience level, tools and tech stack, languages you can work in and your level, available dates, time zone and daily working hours, motivation, 18+ confirmation, how you heard about us (optional)',
        'To select participants, build teams, send results and dates, and confirm your spot',
        "6 months after selection ends. If you are not selected, or your spot is canceled because you didn't reply within 24 hours, we delete it right away.",
      ],
      refuse: "You can choose not to agree, but then you can't apply.",
    },
    rulesConfirmed: 'I have read the rules for finishing and dropping out in the Program Guide.',
    rulesLink: 'See the rules',
    pilotUnderstood: 'I understand this is a free pilot.',
    pilotDetail:
      'Some features may not be finished, and we may ask you to write a review after the project ends.',
    marketingOptIn: 'Send me emails about Koready news, like the next program.',
    marketingDetail:
      'You can unsubscribe at any time. Your choice does not affect your application.',
    submitHint: 'Check all required items to apply.',
    submit: 'Apply',
    submitting: 'Applying…',
    errors: {
      required: 'Please answer this question.',
      login: 'Please log in with your Koready account.',
      email: 'Please enter a valid email address.',
      dates: 'Select at least 1 date.',
      level: 'Choose a level for each language you selected.',
      hours: 'Your end time must be after your start time.',
      hoursSame: 'Your start and end times can’t be the same.',
      hoursOverlap: 'Your time ranges can’t overlap.',
      tooLong: 'Please keep your answer under 300 characters.',
      check: 'Please check this box to apply.',
      duplicate: "You've already applied with this email. We'll email you with your result.",
      server: "We couldn't send your application. Check your connection and try again.",
    },
    done: {
      title: 'Application received',
      body: "We'll email you within 7 business days with your result and your team's dates. If you're selected, please reply within 24 hours. If we don't hear from you, we may offer your spot to another applicant.",
      cta: 'Take the 3-minute self-check',
    },
  },
};

const ROLES = ['planner', 'designer', 'developer', 'marketer'];
const EXPERIENCE = ['student', 'under_1', '1_3', 'over_3'];
const TOOLS = [
  'Figma',
  'Notion',
  'Jira',
  'Slack',
  'Google Workspace',
  'React',
  'Next.js',
  'TypeScript',
  'Node.js',
  'Spring',
  'Python',
  'Flutter',
  'Canva',
  'Google Analytics',
  'Meta Ads',
];
const LANGUAGE_LEVELS = ['Basic', 'Conversational', 'Fluent'];
const REFERRALS = ['instagram', 'meta_ad', 'friend', 'community', 'other'];
const MAX_MOTIVATION = 300;
/** 보조 입력칸은 속한 질문의 오류를 지운다 */
const ERROR_KEY = { toolsOther: 'tools', otherTools: 'tools', otherLanguage: 'languages' };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* ── 작은 부품 ───────────────────────────────────────────── */

const Section = ({ children }) => (
  <section className='flex flex-col gap-6 rounded-2xl border border-green-deep03/[0.12] bg-grayscale-gray00 p-5 sm:p-7'>
    {children}
  </section>
);

/** 질문 하나 — 번호 없는 굵은 질문 + 도움말 + 내용 + 오류 */
const Question = ({ label, required, optionalLabel, hint, htmlFor, error, children }) => (
  <div data-error={!!error} className='flex flex-col gap-2.5'>
    <div className='flex flex-col gap-1'>
      <label htmlFor={htmlFor} className='text-[14px] font-medium leading-[140%] text-text-primary'>
        {label}
        {required && <span className='text-functional-failed03'>*</span>}
        {optionalLabel && (
          <span className='font-normal text-text-secondary'> ({optionalLabel})</span>
        )}
      </label>
      {hint && <span className='text-[12px] leading-[150%] text-text-secondary'>{hint}</span>}
    </div>
    {children}
    {error && <p className='text-[12px] text-functional-failed04'>{error}</p>}
  </div>
);

const Chip = ({ selected, onClick, children }) => (
  <button
    type='button'
    aria-pressed={selected}
    onClick={onClick}
    className={cn(
      'h-9 rounded-full border px-3.5 text-[14px] transition-colors',
      selected
        ? 'border-green-deep02 bg-surface-green10 font-medium text-green-deep04'
        : 'border-green-deep03/20 bg-grayscale-gray00 text-text-primary hover:bg-grayscale-gray01',
    )}
  >
    {children}
  </button>
);

const Check = ({ checked, onChange, error, children }) => (
  <div data-error={!!error} className='flex flex-col gap-1'>
    {/* 공통 Checkbox 는 한 줄 높이(h-5)라 여러 줄 문구에 맞게 높이·정렬을 푼다 */}
    <Checkbox
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      label={children}
      className='!h-auto !items-start gap-2.5'
      boxClassName='shrink-0'
      labelClassName='text-[14px] leading-[150%] text-text-primary'
    />
    {error && <p className='pl-[30px] text-[12px] text-functional-failed04'>{error}</p>}
  </div>
);

/** 언어 토글 — 한국어 / English. ?lang=en 으로 링크를 나눠 쓸 수 있다 */
/** 동의 항목 앞 [필수]·[선택]·[전체 동의] */
const Tag = ({ optional, children }) => (
  <span className={optional ? 'text-text-secondary' : 'text-green-deep03'}>[{children}]</span>
);

/** 동의 문구 아래 문서 링크 줄 — 새 탭으로 열어 쓰던 신청서를 잃지 않는다 */
const Links = ({ children }) => (
  <span className='mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[13px] text-text-secondary'>
    {children}
  </span>
);
const DocLink = ({ href, children }) => (
  <a
    href={href}
    target='_blank'
    rel='noopener noreferrer'
    className='font-medium text-green-deep03 underline underline-offset-2'
  >
    {children}
  </a>
);

export const LangToggle = ({ lang, onChange }) => (
  <div
    role='group'
    aria-label='Language'
    className='flex shrink-0 gap-0.5 rounded-full bg-grayscale-gray02/70 p-1 text-[13px]'
  >
    {[
      ['ko', '한국어'],
      ['en', 'English'],
    ].map(([key, label]) => (
      <button
        key={key}
        type='button'
        aria-pressed={lang === key}
        onClick={() => onChange(key)}
        className={cn(
          'h-7 rounded-full px-3 transition-colors',
          lang === key
            ? 'bg-grayscale-gray00 font-medium text-text-primary shadow-[0_1px_3px_rgba(0,0,0,0.12)]'
            : 'text-text-secondary hover:text-text-primary',
        )}
      >
        {label}
      </button>
    ))}
  </div>
);

/* ── 폼 상태 ─────────────────────────────────────────────── */

const initial = (isLoggedIn) => ({
  fullName: '',
  // 로그인한 채로 들어오면 계정이 있는 것으로 시작한다
  hasAccount: isLoggedIn ? 'yes' : '',
  email: '',
  role: '',
  experience: '',
  tools: [],
  toolsOther: false, // '기타' 칩 — 켜면 입력칸이 생긴다
  otherTools: '',
  languages: [], // 고른 언어 키: 'English' · 'Korean' · 'Other'
  levels: {}, // 언어 키 → 수준
  otherLanguage: '',
  dates: [],
  timezone: guessTimezone(),
  // 하루 작업 시간 — 여러 구간 (예: 10:00–12:00, 15:00–20:00) · 최대 5개
  slots: [{ from: '09:00', to: '18:00' }],
  motivation: '',
  referral: '',
  referralOther: '', // 유입 경로 '기타' 를 고르면 받는 내용
  ageConfirmed: false,
  termsAgreed: false,
  dataConsent: false,
  rulesConfirmed: false,
  pilotUnderstood: false,
  marketingOptIn: false, // [선택] 기본 꺼짐
});

const accountEmailOf = (user) => user?.email || user?.socialEmail || '';
/** 실제로 보낼 이메일 — 계정이 있으면 로그인한 계정 이메일 */
const emailFor = (f, user) => (f.hasAccount === 'yes' ? accountEmailOf(user) : f.email.trim());

const validate = (f, user, isLoggedIn, t) => {
  const e = {};
  const req = t.errors.required;
  if (!f.fullName.trim()) e.fullName = req;
  if (!f.hasAccount) e.hasAccount = req;
  else if (f.hasAccount === 'yes' && !isLoggedIn) e.hasAccount = t.errors.login;
  else if (f.hasAccount === 'no' && !f.email.trim()) e.email = req;
  else if (!EMAIL_RE.test(emailFor(f, user))) e.email = t.errors.email;
  if (!f.role) e.role = req;
  if (!f.experience) e.experience = req;
  if (f.tools.length === 0 && !(f.toolsOther && f.otherTools.trim())) e.tools = req;
  if (f.languages.length === 0) e.languages = req;
  else if (
    f.languages.some((l) => !f.levels[l]) ||
    (f.languages.includes('Other') && !f.otherLanguage.trim())
  )
    e.languages = t.errors.level;
  if (f.dates.length === 0) e.dates = t.errors.dates;
  if (!f.timezone) e.hours = req;
  else {
    const err = slotsError(f.slots, {
      overnight: false,
      messages: {
        empty: req,
        same: t.errors.hoursSame,
        order: t.errors.hours,
        overlap: t.errors.hoursOverlap,
      },
    });
    if (err) e.hours = err;
  }
  if (!f.motivation.trim()) e.motivation = req;
  else if (f.motivation.length > MAX_MOTIVATION) e.motivation = t.errors.tooLong;
  REQUIRED_CHECKS.forEach((k) => {
    if (!f[k]) e[k] = t.errors.check;
  });
  return e;
};

const toPayload = (f, user) => ({
  fullName: f.fullName.trim(),
  email: emailFor(f, user),
  timezone: f.timezone,
  role: f.role,
  experience: f.experience,
  tools: [
    ...f.tools,
    ...(f.toolsOther ? f.otherTools : '')
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean),
  ],
  languages: f.languages.map((l) => ({
    name: l === 'Other' ? f.otherLanguage.trim() : l,
    level: f.levels[l],
  })),
  dates: f.dates,
  // 첫 구간은 예전 필드로도 보낸다
  availableFrom: f.slots[0].from,
  availableTo: f.slots[0].to,
  availableSlots: f.slots,
  motivation: f.motivation.trim(),
  // 기타는 적은 내용을 붙여 보낸다 (예: "other: YouTube")
  referral:
    f.referral === 'other' && f.referralOther.trim()
      ? `other: ${f.referralOther.trim()}`.slice(0, 100)
      : f.referral || null,
  ageConfirmed: f.ageConfirmed,
  termsAgreed: f.termsAgreed,
  dataConsent: f.dataConsent,
  rulesConfirmed: f.rulesConfirmed,
  pilotUnderstood: f.pilotUnderstood,
  marketingOptIn: f.marketingOptIn,
});

/* ── 화면 ───────────────────────────────────────────────── */

const Done = ({ t }) => (
  <div className='mx-auto flex w-full max-w-[560px] flex-col items-center gap-4 rounded-2xl border border-green-deep03/[0.12] bg-grayscale-gray00 px-6 py-12 text-center'>
    <span className='flex h-12 w-12 items-center justify-center rounded-xl bg-green-deep02 text-[15px] font-medium text-grayscale-gray00'>
      2d
    </span>
    <h1 className='text-[24px] font-medium leading-[130%] text-text-primary'>{t.done.title}</h1>
    <p className='text-[15px] leading-[160%] text-text-secondary'>
      {t.done.body}
    </p>
    <a
      href={SKILLS_SELF_CHECK_URL}
      className='mt-2 inline-flex h-11 items-center rounded-full bg-surface-green10 px-5 text-[15px] font-medium text-text-primary hover:bg-green-light02/60'
    >
      {t.done.cta}
    </a>
  </div>
);

const TwoDaysApply = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const lang = params.get('lang') === 'en' ? 'en' : 'ko';
  const t = COPY[lang];
  const { user, isLoggedIn, loading } = useAuth() ?? {};
  const [form, setForm] = useState(() => initial(isLoggedIn));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [done, setDone] = useState(false);
  const timezones = useMemo(listTimezones, []);

  // 로그인 정보가 늦게 오면(새로고침 직후) 계정 질문을 채워 둔다 — 이미 고른 답은 건드리지 않는다
  const hasAccount = form.hasAccount || (!loading && isLoggedIn ? 'yes' : '');

  const setLang = (next) => {
    const p = new URLSearchParams(params);
    if (next === 'en') p.set('lang', 'en');
    else p.delete('lang');
    setParams(p, { replace: true });
    // 이미 보인 오류 문구도 새 언어로 바꾼다
    if (Object.keys(errors).length)
      setErrors(validate({ ...form, hasAccount }, user, isLoggedIn, COPY[next]));
    setServerError('');
  };
  // 동의 — 전체 동의는 선택 항목까지 한 번에, 개별로 다시 풀 수 있다
  const allChecked = ALL_CHECKS.every((k) => form[k]);
  const requiredChecked = REQUIRED_CHECKS.every((k) => form[k]);
  const setAll = (value) => {
    setForm((f) => ({ ...f, ...Object.fromEntries(ALL_CHECKS.map((k) => [k, value])) }));
    if (value)
      setErrors((e) => ({
        ...e,
        ...Object.fromEntries(REQUIRED_CHECKS.map((k) => [k, undefined])),
      }));
  };
  const langQuery = lang === 'en' ? '?lang=en' : '';
  const set = (key) => (value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [ERROR_KEY[key] ?? key]: undefined }));
  };
  // 시간대·시작·종료는 한 질문(hours)이라 오류도 함께 지운다
  const setHours = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, hours: undefined }));
  };
  const toggleIn = (key, value) =>
    set(key)(
      form[key].includes(value) ? form[key].filter((x) => x !== value) : [...form[key], value],
    );
  const setLevel = (name, level) => {
    setForm((f) => ({ ...f, levels: { ...f.levels, [name]: level } }));
    setErrors((e) => ({ ...e, languages: undefined }));
  };
  const goLogin = () => {
    const returnTo = `/2days/apply${lang === 'en' ? '?lang=en' : ''}`;
    saveAuthReturnTo(returnTo);
    navigate('/login', { state: { returnTo } });
  };

  const submit = async (e) => {
    e.preventDefault();
    const current = { ...form, hasAccount };
    const found = validate(current, user, isLoggedIn, t);
    setErrors(found);
    setServerError('');
    if (Object.keys(found).length) {
      // 첫 번째 오류로 스크롤
      requestAnimationFrame(() =>
        document
          .querySelector('[data-error="true"]')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
      );
      return;
    }
    setSubmitting(true);
    try {
      await submitPilotApplication(toPayload(current, user));
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      setServerError(err.response?.status === 409 ? t.errors.duplicate : t.errors.server);
    } finally {
      setSubmitting(false);
    }
  };

  const levelSelect = (name, label) => (
    <Select
      aria-label={`${label} ${t.level}`}
      value={form.levels[name] ?? ''}
      onChange={(e) => setLevel(name, e.target.value)}
      className='w-[160px] shrink-0'
    >
      <option value=''>{t.level}</option>
      {LANGUAGE_LEVELS.map((l) => (
        <option key={l} value={l}>
          {t.levels[l]}
        </option>
      ))}
    </Select>
  );

  return (
    <div lang={lang} className='min-h-screen bg-bg-twodays px-4 py-10 sm:py-14'>
      {done ? (
        <Done t={t} />
      ) : (
        <form
          noValidate
          onSubmit={submit}
          className='mx-auto flex w-full max-w-[640px] flex-col gap-5'
        >
          <header className='flex flex-col gap-3 px-1 pb-2'>
            <div className='flex items-center justify-between gap-3'>
              <span className='text-[13px] font-medium text-green-deep03'>
                2days project · Pilot
              </span>
              <LangToggle lang={lang} onChange={setLang} />
            </div>
            <h1 className='text-[28px] font-medium leading-[125%] tracking-[-0.01em] text-text-primary sm:text-[32px]'>
              {t.title}
            </h1>
            <p className='whitespace-pre-line text-[15px] leading-[160%] text-text-secondary'>
              {t.intro}
            </p>
          </header>

          {/* 1~2 기본 정보 */}
          <Section>
            <Question label={t.fullName} required htmlFor='apply-name' error={errors.fullName}>
              <TextInput
                id='apply-name'
                autoComplete='name'
                placeholder={t.fullNamePlaceholder}
                value={form.fullName}
                onChange={(e) => set('fullName')(e.target.value)}
              />
            </Question>

            <Question label={t.hasAccount} required error={errors.hasAccount}>
              <RadioGroup
                ariaLabel={t.hasAccount}
                value={hasAccount}
                onChange={set('hasAccount')}
                options={t.yesNo}
              />
              {/* 예: 로그인한 계정 이메일로 받는다. 로그인 전이면 로그인부터 */}
              {hasAccount === 'yes' &&
                (isLoggedIn ? (
                  <p className='rounded-lg bg-surface-green10 px-3 py-2.5 text-[13px] leading-[150%] text-green-deep04'>
                    {t.accountEmail(accountEmailOf(user))}
                  </p>
                ) : (
                  <TdButton onClick={goLogin} className='w-fit'>
                    {t.login}
                  </TdButton>
                ))}
            </Question>

            {/* 아니요: 선발 결과를 받을 이메일 */}
            {hasAccount === 'no' && (
              <Question
                label={t.email}
                required
                hint={t.emailHint}
                htmlFor='apply-email'
                error={errors.email}
              >
                <TextInput
                  id='apply-email'
                  type='email'
                  autoComplete='email'
                  placeholder='name@example.com'
                  value={form.email}
                  onChange={(e) => set('email')(e.target.value)}
                />
              </Question>
            )}
          </Section>

          {/* 3~6 역할·경험·협업 */}
          <Section>
            <Question label={t.role} required error={errors.role}>
              <RadioGroup
                ariaLabel={t.role}
                value={form.role}
                onChange={set('role')}
                options={ROLES.map((v) => ({ value: v, label: t.roles[v] }))}
              />
            </Question>

            <Question label={t.experience} required error={errors.experience}>
              <RadioGroup
                ariaLabel={t.experience}
                value={form.experience}
                onChange={set('experience')}
                options={EXPERIENCE.map((v) => ({ value: v, label: t.experiences[v] }))}
              />
            </Question>

            <Question label={t.tools} required hint={t.toolsHint} error={errors.tools}>
              <div className='flex flex-wrap gap-2'>
                {TOOLS.map((tool) => (
                  <Chip
                    key={tool}
                    selected={form.tools.includes(tool)}
                    onClick={() => toggleIn('tools', tool)}
                  >
                    {tool}
                  </Chip>
                ))}
                <Chip
                  selected={form.toolsOther}
                  onClick={() => set('toolsOther')(!form.toolsOther)}
                >
                  {t.other}
                </Chip>
              </div>
              {form.toolsOther && (
                <TextInput
                  autoFocus
                  aria-label={t.toolsOtherPlaceholder}
                  placeholder={t.toolsOtherPlaceholder}
                  value={form.otherTools}
                  onChange={(e) => set('otherTools')(e.target.value)}
                />
              )}
            </Question>

            <Question label={t.languages} required hint={t.languagesHint} error={errors.languages}>
              <ul className='flex flex-col gap-2.5'>
                {['English', 'Korean', 'Other'].map((name) => {
                  const on = form.languages.includes(name);
                  const label = name === 'Other' ? t.other : t.languageNames[name];
                  return (
                    <li key={name} className='flex min-h-10 flex-wrap items-center gap-x-3 gap-y-2'>
                      <Checkbox
                        checked={on}
                        onChange={() => toggleIn('languages', name)}
                        label={label}
                        className='w-[96px] shrink-0 gap-2.5'
                        labelClassName='text-[14px] text-text-primary'
                      />
                      {on && name === 'Other' && (
                        <TextInput
                          aria-label={t.languageOtherPlaceholder}
                          placeholder={t.languageOtherPlaceholder}
                          value={form.otherLanguage}
                          onChange={(e) => set('otherLanguage')(e.target.value)}
                          className='w-[140px] shrink-0'
                        />
                      )}
                      {on && levelSelect(name, label)}
                    </li>
                  );
                })}
              </ul>
            </Question>
          </Section>

          {/* 7~8 일정 */}
          <Section>
            <Question label={t.dates} required hint={t.datesHint} error={errors.dates}>
              <div className='flex flex-col gap-2'>
                {DATE_SLOTS.map((slot) => {
                  const on = form.dates.includes(slot.value);
                  return (
                    <Checkbox
                      key={slot.value}
                      checked={on}
                      onChange={() => toggleIn('dates', slot.value)}
                      label={slot[lang]}
                      className={cn(
                        '!h-11 gap-2.5 rounded-lg border px-3 transition-colors',
                        on
                          ? 'border-green-deep02 bg-surface-green10'
                          : 'border-green-deep03/20 hover:bg-grayscale-gray01',
                      )}
                      labelClassName='text-[14px] text-text-primary'
                    />
                  );
                })}
              </div>
            </Question>

            <Question label={t.hours} required hint={t.hoursHint} error={errors.hours}>
              <div className='flex flex-col gap-2'>
                <Select
                  aria-label={t.timezone}
                  value={form.timezone}
                  onChange={(e) => setHours('timezone', e.target.value)}
                >
                  {!form.timezone && <option value=''>{t.timezone}</option>}
                  {timezones.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz.replace(/_/g, ' ')} ({offsetOf(tz)})
                    </option>
                  ))}
                </Select>
                <HoursRanges
                  slots={form.slots}
                  onChange={(slots) => setHours('slots', slots)}
                  fieldClassName='h-11 min-w-0 flex-1 sm:w-[124px] sm:flex-none'
                  overnight={false}
                  fromLabel={t.from}
                  toLabel={t.to}
                  addLabel={t.addHours}
                  removeLabel={t.removeHours}
                />
              </div>
            </Question>
          </Section>

          {/* 9~10 동기·유입 */}
          <Section>
            <Question
              label={t.motivation}
              required
              hint={t.motivationHint}
              htmlFor='apply-motivation'
              error={errors.motivation}
            >
              <TextArea
                id='apply-motivation'
                maxLength={MAX_MOTIVATION}
                placeholder={t.motivationPlaceholder}
                value={form.motivation}
                onChange={(e) => set('motivation')(e.target.value)}
              />
              <span className='-mt-1 self-end text-[12px] text-text-secondary'>
                {form.motivation.length}/{MAX_MOTIVATION}
              </span>
            </Question>

            <Question label={t.referral} optionalLabel={t.optional} htmlFor='apply-referral'>
              <Select
                id='apply-referral'
                value={form.referral}
                onChange={(e) => set('referral')(e.target.value)}
              >
                <option value=''>{t.select}</option>
                {REFERRALS.map((r) => (
                  <option key={r} value={r}>
                    {t.referrals[r]}
                  </option>
                ))}
              </Select>
              {form.referral === 'other' && (
                <TextInput
                  autoFocus
                  aria-label={t.referralOtherPlaceholder}
                  placeholder={t.referralOtherPlaceholder}
                  maxLength={90}
                  value={form.referralOther}
                  onChange={(e) => set('referralOther')(e.target.value)}
                />
              )}
            </Question>
          </Section>

          {/* 11~16 확인 — 신청서 최종 동의서 (2026-10-13) */}
          <Section>
            <div className='flex flex-col gap-1.5'>
              <h2 className='text-[14px] font-medium leading-[140%] text-text-primary'>
                {t.confirm}
              </h2>
              <p className='text-[13px] leading-[150%] text-text-secondary'>{t.replyNotice}</p>
            </div>
            <div className='flex flex-col gap-3'>
              {/* 전체 동의 — 눌러도 하나씩 풀 수 있다 */}
              <div className='rounded-xl bg-green-deep03/[0.04] px-3 py-2.5'>
                <Check checked={allChecked} onChange={setAll}>
                  <span className='font-medium'>
                    <Tag>{t.tags.all}</Tag> {t.agreeAll}
                  </span>
                </Check>
              </div>
              <Check
                checked={form.ageConfirmed}
                onChange={set('ageConfirmed')}
                error={errors.ageConfirmed}
              >
                <Tag>{t.tags.required}</Tag> {t.ageConfirmed}
              </Check>
              <Check
                checked={form.termsAgreed}
                onChange={set('termsAgreed')}
                error={errors.termsAgreed}
              >
                <Tag>{t.tags.required}</Tag> {t.termsAgreed}
                <Links>
                  <DocLink href={`/2days/terms${langQuery}`}>{t.termsLink}</DocLink>
                  <span aria-hidden>·</span>
                  <DocLink href={GUIDE_URL}>{t.guideLink}</DocLink>
                </Links>
              </Check>
              <Check
                checked={form.dataConsent}
                onChange={set('dataConsent')}
                error={errors.dataConsent}
              >
                <Tag>{t.tags.required}</Tag> {t.dataConsent}
                <Links>
                  <DocLink href={`/2days/privacy${langQuery}`}>{t.privacyLink}</DocLink>
                  <span aria-hidden>·</span>
                  <button
                    type='button'
                    aria-expanded={showDetails}
                    onClick={(e) => {
                      e.preventDefault();
                      setShowDetails((v) => !v);
                    }}
                    className='font-medium text-green-deep03 underline underline-offset-2'
                  >
                    {showDetails ? t.hideDetails : t.viewDetails} {showDetails ? '▴' : '▾'}
                  </button>
                </Links>
                {showDetails && (
                  <span className='mt-2 block overflow-x-auto rounded-lg border border-grayscale-gray03 text-[12px] leading-[150%] text-text-secondary'>
                    <table className='w-full min-w-[480px] border-collapse text-left'>
                      <thead className='bg-grayscale-gray01 text-text-primary'>
                        <tr>
                          {t.dataTable.headers.map((h) => (
                            <th
                              key={h}
                              className='border-b border-grayscale-gray03 px-3 py-2 font-medium'
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {t.dataTable.row.map((c) => (
                            <td key={c} className='px-3 py-2 align-top'>
                              {c}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                    <span className='block border-t border-grayscale-gray03 px-3 py-2'>
                      · {t.dataTable.refuse}
                    </span>
                  </span>
                )}
              </Check>
              <Check
                checked={form.rulesConfirmed}
                onChange={set('rulesConfirmed')}
                error={errors.rulesConfirmed}
              >
                <Tag>{t.tags.required}</Tag> {t.rulesConfirmed}
                <Links>
                  {/* 운영 안내 첫 화면이 아니라 10·11번(이탈 기준·완주) 위치로 바로 */}
                  <DocLink href={GUIDE_RULES_URL}>{t.rulesLink}</DocLink>
                </Links>
              </Check>
              <Check
                checked={form.pilotUnderstood}
                onChange={set('pilotUnderstood')}
                error={errors.pilotUnderstood}
              >
                <Tag>{t.tags.required}</Tag> {t.pilotUnderstood}
                <span className='mt-0.5 block text-[13px] text-text-secondary'>
                  {t.pilotDetail}
                </span>
              </Check>
              <Check checked={form.marketingOptIn} onChange={set('marketingOptIn')}>
                <Tag optional>{t.tags.optional}</Tag> {t.marketingOptIn}
                <span className='mt-0.5 block text-[13px] text-text-secondary'>
                  {t.marketingDetail}
                </span>
              </Check>
            </div>
          </Section>

          {serverError && (
            <p className='rounded-xl bg-functional-failed01 px-4 py-3 text-[14px] text-functional-failed04'>
              {serverError}
            </p>
          )}

          <TdButton
            type='submit'
            size='l'
            disabled={submitting || !requiredChecked}
            className={cn('text-[16px]', submitting && 'cursor-wait')}
          >
            {submitting ? t.submitting : t.submit}
          </TdButton>
          {!requiredChecked && (
            <p className='-mt-2 text-center text-[13px] text-text-secondary'>{t.submitHint}</p>
          )}
        </form>
      )}
    </div>
  );
};

export default TwoDaysApply;
